/**
 * Filters an array of files based on supported file types and maximum file size.
 *
 * @param files - The array of files to be filtered.
 * @param supportedFileTypes - An array of supported file extensions (e.g., ['.jpg', '.png']).
 * @param maxFileSize - The maximum allowed file size in bytes.
 * @returns An array of files that match the supported file types and are within the maximum file size.
 */
export const uploadDropFileFilter = (
  files: File[],
  supportedFileTypes: Record<string, string[]>,
  maxFileSize: number,
): File[] => {
  return files.filter(
    (file) =>
      file.size <= maxFileSize && supportedFileTypes[file.type]?.some((ext) => file.name.toLowerCase().endsWith(ext)),
  );
};

/**
 * Default supported file types for the upload drop field.
 * Only ms excel files are supported by default.
 */
export const defaultSupportedFileTypes = {
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  // "text/csv": [".csv"],
};

/**
 * Default maximum file size for the upload drop field.
 * The default value is 10 MB.
 */
export const defaultMaxFileSize = 10 * 1024 * 1024;

/**
 * Reads a file asynchronously and returns its content as an ArrayBuffer.
 *
 * @param {File} file - The file to be read.
 * @returns {Promise<ArrayBuffer | null>} A promise that resolves to the file's content as an ArrayBuffer, or null if the reading fails.
 * @throws {Error} If there is an error reading the file.
 */
export const readFileAsync = async (file: File): Promise<ArrayBuffer | null> => {
  const reader = new FileReader();
  const data = await new Promise<ArrayBuffer | null>((resolve, reject) => {
    reader.onload = (e): void => {
      if (!e.target?.result) {
        reject(new Error("File reading failed"));
        return;
      }
      resolve(e.target.result as ArrayBuffer);
    };
    reader.onerror = (): void => reject(new Error("File reading error"));
    reader.readAsArrayBuffer(file);
  });

  return data;
};

export const fileToBase64String = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (): void => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to convert file to base64 string"));
      }
    };
    reader.onerror = (error: ProgressEvent<FileReader>): void => {
      reject(error);
    };
  });
};
